import { useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Bridges the device theme (localStorage, handled by ThemeContext) with a
// per-account preference. Rendered once inside both AuthProvider and
// ThemeProvider. Logged out → device theme only. Logged in → the profile's
// saved theme_pref overrides the device on load, and any toggle is written back
// to the profile so the choice follows the user across devices.
//
// DB reads are keyed off user id in an effect (NOT inside onAuthStateChange) per
// AUTH_PLAYBOOK. A ref gates the write-back so we never overwrite the saved
// pref before we've loaded it.
export default function ThemeSync() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const loadedFor = useRef(null);

  // On login, pull the saved preference and apply it (it trumps the device).
  useEffect(() => {
    if (!user) { loadedFor.current = null; return; }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('theme_pref')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.theme_pref === 'dark' || data?.theme_pref === 'light') {
          setTheme(data.theme_pref);
        }
        loadedFor.current = user.id; // gate the write-back on after the read
      });
    return () => { cancelled = true; };
  }, [user, setTheme]);

  // While logged in (and only after the initial read for THIS user), persist any
  // theme change to the profile. First run also seeds theme_pref if it was null.
  useEffect(() => {
    if (!user || loadedFor.current !== user.id) return;
    supabase.from('profiles').update({ theme_pref: theme }).eq('id', user.id).then(() => {});
  }, [theme, user]);

  return null;
}
